import React, { useEffect, useMemo, useState } from "react";
import { Tag } from "antd";

import SmartRtpEditFormLoader from "./components/SmartRtpEditFormLoader";
import {
  getGameById,
  getPoolsList,
  saveGame,
} from "../../../controllers/V1/smartRtp/smartRtpController";
import { list as userList } from "../../../controllers/V1/users/usersController";

function formatUserDisplayName(user) {
  const name = [user?.users_name, user?.last_name].filter(Boolean).join(" ").trim();
  return name || String(user?._id || "Unnamed");
}

function formatPosNumber(value) {
  if (value == null || value === "") return "";
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return "";
  return digits.padStart(4, "0");
}

function mapUserToOption(user) {
  const name = formatUserDisplayName(user);
  const pos = formatPosNumber(user?.pos_number);
  const mobile =
    user?.users_mobile != null && user?.users_mobile !== ""
      ? String(user.users_mobile)
      : "";
  const labelParts = [name];
  if (pos) labelParts.push(`POS: ${pos}`);
  if (mobile) labelParts.push(`Mob: ${mobile}`);
  const shortLabel = pos ? `${name} (${pos})` : name;

  return {
    label: labelParts.join(" | "),
    shortLabel,
    value: String(user?._id),
    searchText: [name, pos, mobile, user?.users_type].filter(Boolean).join(" ").toLowerCase(),
    pos,
    mobile,
  };
}

function filterAssignedUserOption(input, option) {
  const query = String(input || "").trim().toLowerCase();
  if (!query) return true;

  const haystack =
    option?.searchText || String(option?.label || "").toLowerCase();
  if (haystack.includes(query)) return true;

  const digits = query.replace(/\D/g, "");
  if (!digits) return false;

  const pos = String(option?.pos || "").replace(/\D/g, "");
  const mobile = String(option?.mobile || "").replace(/\D/g, "");
  return pos.includes(digits) || mobile.includes(digits);
}

const baseFields = [
  { name: "title", label: "Title", rules: [{ required: true, message: "Title is required." }] },
  { name: "prize_title", label: "Prize Title" },
  { name: "game_code", label: "Game Code", rules: [{ required: true, message: "Game code is required." }] },
  { name: "slug", label: "Slug" },
  {
    name: "game_image",
    label: "Game Image",
    type: "image",
    span: 12,
  },
  { name: "ticket_price", label: "Ticket Price", type: "number", props: { min: 0, step: 0.001, precision: 3 }, rules: [{ required: true, message: "Ticket price is required." }] },
  { name: "game_wise_rtp_target", label: "Target RTP %", type: "number", props: { min: 0, max: 100, step: 0.001, precision: 3 } },
  { name: "game_wise_rtp_min", label: "Min RTP %", type: "number", props: { min: 0, max: 100, step: 0.001, precision: 3 } },
  { name: "game_wise_rtp_max", label: "Max RTP %", type: "number", props: { min: 0, max: 100, step: 0.001, precision: 3 } },
  {
    name: "coupon_selection_type",
    label: "Coupon Selection Type",
    type: "select",
    rules: [{ required: true, message: "Coupon selection type is required." }],
    options: [
      { label: "Automatic", value: "Automatic" },
      { label: "Manual", value: "Manual" },
    ],
  },
  {
    name: "number_repeat",
    label: "Number Repeat",
    type: "select",
    rules: [{ required: true, message: "Number repeat is required." }],
    options: [
      { label: "Yes (Y)", value: "Y" },
      { label: "No (N)", value: "N" },
    ],
  },
  {
    name: "enable_one_match_winning",
    label: "1 Match Winning",
    type: "select",
    rules: [{ required: true, message: "1 Match winning is required." }],
    options: [
      { label: "Enable (Y)", value: "Y" },
      { label: "Disable (N)", value: "N" },
    ],
  },
  {
    name: "enable_two_match_winning",
    label: "Two Match Winning",
    type: "select",
    rules: [{ required: true, message: "Two Match winning is required." }],
    options: [
      { label: "Enable (Y)", value: "Y" },
      { label: "Disable (N)", value: "N" },
    ],
  },
  {
    name: "slots_per_ticket",
    label: "Lotto Type",
    type: "number",
    props: { min: 1, max: 20 },
    rules: [{ required: true, message: "Slots per ticket is required." }],
  },
  {
    name: "number_range_min",
    label: "Number Range Min",
    type: "number",
    props: { min: 1 },
    rules: [{ required: true, message: "Number range min is required." }],
  },
  {
    name: "number_range_max",
    label: "Number Range Max",
    type: "number",
    props: { min: 1 },
    rules: [{ required: true, message: "Number range max is required." }],
  },
  {
    name: "max_tickets_per_order",
    label: "Max Tickets Per Order",
    type: "number",
    props: { min: 1, max: 50 },
  },
  {
    name: "show_on",
    label: "Show On",
    type: "multiselect",
    span: 12,
    options: [
      { label: "POS", value: "POS" },
      { label: "App", value: "App" },
      { label: "WebView", value: "WebView" },
    ],
  },
  { name: "description", label: "Description", type: "textarea", span: 24 },
];

export default function SmartRtpGameFormPage() {
  const [userOptions, setUserOptions] = useState([]);
  const [poolOptions, setPoolOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      try {
        const listData = await userList({
          type: "",
          condition: {},
          select: {},
          sort: { users_name: 1 },
          skip: 0,
          limit: 0,
        });
        if (cancelled) return;
        if (listData.status === true && Array.isArray(listData.result)) {
          setUserOptions(listData.result.map(mapUserToOption));
        } else {
          setUserOptions([]);
        }
      } catch (error) {
        if (!cancelled) setUserOptions([]);
      }
    };
    const loadPools = async () => {
      try {
        const res = await getPoolsList({
          type: "",
          condition: {},
          limit: 0,
          skip: 0,
          sort: { title: 1 },
        });
        if (cancelled) return;
        if (res.status === true) {
          setPoolOptions(
            (res.result || []).map((pool) => ({
              label: `${pool.title || pool.pool_code || "Pool"} (${pool.pool_code || pool._id}) · bal ${pool.current_balance ?? 0}`,
              value: String(pool._id),
            }))
          );
        }
      } catch (error) {
        if (!cancelled) setPoolOptions([]);
      }
    };
    loadUsers();
    loadPools();
    return () => {
      cancelled = true;
    };
  }, []);

  const renderAssignedUserTag = useMemo(
    () => (tagProps) => {
      const { label, value, closable, onClose } = tagProps;
      const option = userOptions.find((item) => item.value === String(value));
      const text = option?.shortLabel || label;
      const fullLabel = option?.label || label;

      return (
        <Tag
          closable={closable}
          onClose={onClose}
          style={{
            marginInlineEnd: 4,
            marginBlock: 2,
            maxWidth: 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={fullLabel}
        >
          {text}
        </Tag>
      );
    },
    [userOptions]
  );

  const fields = [
    ...baseFields.slice(0, 6),
    {
      name: "prize_pool_id",
      label: "Prize Pool (optional)",
      type: "select",
      span: 12,
      props: {
        allowClear: true,
        placeholder: "Optional — link later or when creating the pool",
      },
      options: poolOptions,
    },
    ...baseFields.slice(6, -1),
    {
      name: "assigned_user",
      label: "Assigned User",
      type: "multiselect",
      span: 24,
      formItemStyle: { marginBottom: 24 },
      props: {
        allowClear: true,
        showSearch: true,
        filterOption: filterAssignedUserOption,
        placeholder: "Search by name, POS number, or mobile (empty = all users)",
        maxTagCount: 4,
        maxTagPlaceholder: (omittedValues) => `+${omittedValues.length} more`,
        tagRender: renderAssignedUserTag,
        style: { width: "100%" },
        popupMatchSelectWidth: false,
        listHeight: 320,
        styles: {
          selector: {
            minHeight: 32,
            height: "auto",
          },
        },
      },
      options: userOptions,
    },
    baseFields[baseFields.length - 1],
  ];

  return (
    <SmartRtpEditFormLoader
      titlePrefix="Smart RTP Game"
      backTo="/smart-rtp/games"
      fields={fields}
      defaultValues={{
        coupon_selection_type: "Automatic",
        number_repeat: "N",
        enable_one_match_winning: "Y",
        enable_two_match_winning: "Y",
        slots_per_ticket: 6,
        number_range_min: 1,
        number_range_max: 25,
        max_tickets_per_order: 10,
        assigned_user: [],
        prize_pool_id: "",
        prize_title: "",
      }}
      fetchById={getGameById}
      mapInitial={(row) => ({
        ...row,
        coupon_selection_type: row?.coupon_selection_type || "Automatic",
        number_repeat: row?.number_repeat || "N",
        enable_one_match_winning: row?.enable_one_match_winning || "Y",
        enable_two_match_winning: row?.enable_two_match_winning || "Y",
        slots_per_ticket: row?.slots_per_ticket ?? 6,
        number_range_min: row?.number_range_min ?? 1,
        number_range_max: row?.number_range_max ?? 25,
        max_tickets_per_order: row?.max_tickets_per_order ?? 10,
        ticket_price: row?.ticket_price != null ? Number(row.ticket_price) : undefined,
        game_wise_rtp_target:
          row?.game_wise_rtp_target != null ? Number(row.game_wise_rtp_target) : undefined,
        game_wise_rtp_min:
          row?.game_wise_rtp_min != null ? Number(row.game_wise_rtp_min) : undefined,
        game_wise_rtp_max:
          row?.game_wise_rtp_max != null ? Number(row.game_wise_rtp_max) : undefined,
        assigned_user: Array.isArray(row?.assigned_user)
          ? row.assigned_user.map(String)
          : [],
        prize_pool_id: row?.prize_pool_id ? String(row.prize_pool_id) : "",
        prize_title: row?.prize_title || "",
      })}
      onSave={(values, editId) =>
        saveGame({
          ...values,
          assigned_user: Array.isArray(values.assigned_user) ? values.assigned_user : [],
          prize_pool_id: values.prize_pool_id || null,
          prize_title: values.prize_title || "",
          enable_one_match_winning: values.enable_one_match_winning || "Y",
          enable_two_match_winning: values.enable_two_match_winning || "Y",
          ticket_price: values.ticket_price,
          game_wise_rtp_target: values.game_wise_rtp_target,
          game_wise_rtp_min: values.game_wise_rtp_min,
          game_wise_rtp_max: values.game_wise_rtp_max,
          slots_per_ticket: Number(values.slots_per_ticket),
          number_range_min: Number(values.number_range_min),
          number_range_max: Number(values.number_range_max),
          max_tickets_per_order:
            values.max_tickets_per_order != null && values.max_tickets_per_order !== ""
              ? Number(values.max_tickets_per_order)
              : undefined,
          ...(editId ? { editId } : {}),
        })
      }
    />
  );
}
